import { Component, inject } from '@angular/core'
import { CommonModule } from '@angular/common'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { LoginService } from '../../shared/login.service'
import { NgInvisibleReCaptchaModule } from '@mycicero/ng-invisible-re-captcha'
import { RecaptchaService } from '../../../shared/recaptcha/recaptcha.service'
import { ExternalProviderEnum, ExternalProviderEnumArray } from '../external-provider.enum'
import { catchError } from 'rxjs/operators'
import { appConfig } from '../../../../brands/_current/config/app-config'
import { EMPTY, noop } from 'rxjs'
import { ActivatedRoute, Params, Router } from '@angular/router'
import { HttpErrorResponse } from '@angular/common/http'
import { CodeError } from '../../../shared/mycicero-web-api/shared/base-output.model'
import { MOONEY_AUTHORIZATION_CODE_QUERY_PARAM } from '../external-provider-mooney.const'
import { isExternalProviderState } from '../external-provider-state.model'
import { SSO_RETURN_URL_PARAM } from '../../shared/login-query-params'

export const STATE_QUERY_PARAM = 'state'
export const PROVIDER_QUERY_PARAM = 'provider'

@Component({
  selector: 'app-external-login-back-redirect-page',
  standalone: true,
  imports: [CommonModule, ProgressSpinnerModule, NgInvisibleReCaptchaModule],
  templateUrl: './external-login-back-redirect-page.component.html',
  styleUrls: ['./external-login-back-redirect-page.component.scss'],
})
export class ExternalLoginBackRedirectPageComponent {
  private readonly loginService = inject(LoginService)
  private readonly recaptchaService = inject(RecaptchaService)
  recaptchaSiteKey$ = this.recaptchaService.getRecaptchaKey()
  private readonly router = inject(Router)
  private readonly route = inject(ActivatedRoute)
  private recaptchaId: number | null = null

  onRecaptchaError() {
    this.recaptchaService.handleRecaptchaError()
  }

  onRecaptchaLoaded(id: number) {
    this.recaptchaId = id
    this.recaptchaService.execute(this.recaptchaId)
  }

  onRecaptchaSolved(recaptchaValue: string) {
    const stateKey = this.route.snapshot.queryParams[STATE_QUERY_PARAM] ?? ''
    const state = JSON.parse(window.sessionStorage.getItem(stateKey) || null)
    const provider = ExternalProviderEnumArray.find(
      (value) => value.toLowerCase() === this.route.snapshot.params[PROVIDER_QUERY_PARAM]?.toLowerCase(),
    )

    if (!provider || !isExternalProviderState(state)) {
      this.goToGenericErrorPage()
    } else {
      const authorizationCode = this.getAuthorizationCodeBasedOnProvider(provider)
      window.sessionStorage.removeItem(stateKey)
      this.loginService
        .checkExternalLogin(provider, state.code_verifier, authorizationCode, state.returnUrl, recaptchaValue)
        .pipe(
          catchError((response) => {
            this.recaptchaService.reset(this.recaptchaId)
            if (
              response instanceof HttpErrorResponse &&
              (response.error.CodeError as CodeError) === CodeError.accountAlreadyExists
            ) {
              this.router
                .navigate(
                  [
                    '/',
                    appConfig.login.paths._root,
                    appConfig.login.externalProvider.paths._root,
                    provider,
                    appConfig.login.externalProvider.paths.error,
                  ],
                  { queryParams: { [SSO_RETURN_URL_PARAM]: state.returnUrl } },
                )
                .then(noop)
            } else {
              this.goToGenericErrorPage(state.returnUrl)
            }
            return EMPTY
          }),
        )
        .subscribe()
    }
  }

  onRecaptchaExpired() {
    this.recaptchaService.reset(this.recaptchaId)
  }

  private goToGenericErrorPage(returnUrl?: string) {
    const queryParams: Params = !returnUrl ? {} : { [SSO_RETURN_URL_PARAM]: returnUrl }
    this.router
      .navigate(['/', appConfig.feedback.paths._root, appConfig.feedback.paths.genericError], {
        queryParams,
      })
      .then(noop)
  }

  private getAuthorizationCodeBasedOnProvider(provider: ExternalProviderEnum): string {
    if (provider === ExternalProviderEnumArray[2]) {
      return this.route.snapshot.queryParams[MOONEY_AUTHORIZATION_CODE_QUERY_PARAM]
    }

    return ''
  }
}
